import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '@/lib/utils'
import type { Application } from '@/types/models'

// ---------------------------------------------------------------------------
// Deadline color helper
// ---------------------------------------------------------------------------

function deadlineColor(deadline?: string): string {
  if (!deadline) return 'text-muted-foreground'
  const diff = new Date(deadline).getTime() - Date.now()
  const days = diff / (1000 * 60 * 60 * 24)
  if (days <= 7) return 'text-red-500 font-semibold'
  if (days <= 30) return 'text-yellow-600 font-medium'
  return 'text-muted-foreground'
}

// ---------------------------------------------------------------------------
// Priority border
// ---------------------------------------------------------------------------

const PRIORITY_BORDER: Record<string, string> = {
  HIGH: 'border-l-4 border-l-red-500',
  MEDIUM: 'border-l-4 border-l-yellow-500',
  LOW: 'border-l-4 border-l-muted-foreground/30',
}

// ---------------------------------------------------------------------------
// ApplicationCard
// ---------------------------------------------------------------------------

interface Props {
  application: Application
  onClick: () => void
}

/**
 * ApplicationCard — a single card in the Kanban board.
 *
 * Uses `@dnd-kit/sortable` to be draggable within and between columns.
 * Clicking the card (without dragging) opens the ApplicationDrawer.
 */
export default function ApplicationCard({ application, onClick }: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: application.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const progress =
    application.totalRequirements > 0
      ? Math.round((application.completedRequirements / application.totalRequirements) * 100)
      : 0

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={cn(
        'cursor-pointer select-none rounded-md border bg-card px-3 py-3 shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        PRIORITY_BORDER[application.priority] ?? PRIORITY_BORDER.LOW,
        isDragging && 'opacity-50 shadow-lg',
      )}
    >
      {/* University + Program */}
      <p className="font-semibold text-sm text-foreground leading-tight truncate">
        {application.universityName}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5 truncate">{application.programName}</p>

      {/* Deadline */}
      {application.deadline && (
        <p className={cn('mt-2 text-xs', deadlineColor(application.deadline))}>
          Due: {new Date(application.deadline).toLocaleDateString()}
        </p>
      )}

      {/* Progress bar */}
      {application.totalRequirements > 0 && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">
              {application.completedRequirements}/{application.totalRequirements} tasks
            </span>
            <span className="text-xs text-muted-foreground">{progress}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-brand-blue transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
